import React from 'react';

import UniversalRouter from 'universal-router';

import { lazyWithSuspense, parseQueryString } from '@travel/utils';

import reservationDetailResolver from 'pages/ReservationDetail/resolver';
import reservationListResolver from 'pages/ReservationList/resolver';

import App from 'core/app';

import paths from './paths';
import type { universalRouterProps } from './types';

const ReservationList = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ReservationList" */ 'pages/ReservationList'),
);
const ReservationDetail = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ReservationDetail" */ 'pages/ReservationDetail'),
);
const ErrorPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ErrorPage" */ 'pages/ErrorPage'),
);

export default (props: universalRouterProps) => {
  const isServer = props.isServer || false;

  const routes = [
    {
      path: '/',
      async action(context: any) {
        let children;

        try {
          children = await context.next();
        } catch (e) {
          console.error(e);
          children = null;
        }

        return (
          <App {...context} key="app">
            {children}
          </App>
        );
      },
      children: [
        {
          path: paths.top.path,
          chunkName: 'pages-ReservationList',
          async action(context: any) {
            if (isServer) {
              await reservationListResolver(props, context);
            } else if (!context.skipResolver) {
              reservationListResolver(props, context);
            }

            // Destructure key from context to avoid React warning about spreading key prop
            const { key: _key, ...contextWithoutKey } = context;
            return <ReservationList {...contextWithoutKey} />;
          },
        },
        {
          path: paths.detail.path,
          chunkName: 'pages-ReservationDetail',
          async action(context: any) {
            if (isServer) {
              await reservationDetailResolver(props, context);
            } else if (!context.skipResolver) {
              reservationDetailResolver(props, context);
            }

            return <ReservationDetail {...context} />;
          },
        },
        {
          // also resolve paths.notFound.path
          path: '(.*)',
          chunkName: 'pages-ErrorPage',
          async action() {
            props.res?.status(400);
            return <ErrorPage />;
          },
        },
      ],
    },
  ];
  return new UniversalRouter(routes, {
    resolveRoute(context, params) {
      if (typeof context.route.action === 'function') {
        context.query = {};
        if (context.search) {
          // when redirect from omni with existing filter,
          // there will be ? added from the redirection
          const searchRegex = new RegExp('\\?$');
          const currentSearch = context.search;
          context.query = parseQueryString(
            searchRegex.test(currentSearch)
              ? currentSearch.substring(0, currentSearch.length - 1)
              : currentSearch,
          );
        }

        // Track initial chunks for SSR asset loading
        if (props.initialChunks && context.route.chunkName) {
          props.initialChunks.push(context.route.chunkName);
        }

        return context.route.action(context, params);
      }
      return undefined;
    },
  });
};
